<template>
  <div class="row mb-3 board-filters-row">
    <div class="col-md-12">
      <div class="c-card board-filters-card">
        <div class="row align-items-center board-filters-row-inner">
          <!-- Client Filter -->
          <div class="col-xl-2 col-md-4 mb-0">
            <v-select
              class="w-100"
              placeholder="Filter by Client"
              :options="clientOptions"
              :value="filters.clientObject"
              @input="onClientSelected"
              code="id"
              label="displayName"
              clearable
              filterable
            />
          </div>
          
          <!-- Work Order Number -->
          <div class="col-xl-1 col-md-3 mb-0 job-number-filter" style="flex: 0 0 10%; max-width: 10%;">
            <input
              type="text"
              class="form-control"
              placeholder="Search by Job #"
              :value="filters.jobNumber"
              @input="onJobNumberChange"
            />
          </div>
          
          <!-- Address Filter -->
          <div class="col-xl-2 col-md-4 mb-0">
            <input
              type="text"
              class="form-control"
              placeholder="Search by Address" 
              :value="filters.address"
              @input="onAddressChange"
            />
          </div>
          
          <!-- Job Type Filter -->
          <div class="col-xl-2 col-md-3 mb-0 job-type-filter" style="flex: 0 0 11%; max-width: 11%;">
            <v-select
              class="w-100"
              placeholder="Job/Project Type"
              :options="jobTypeOptions"
              :value="filters.jobTypeObject"
              @input="onJobTypeSelected"
              code="id"
              label="displayName"
              clearable
              filterable
            />
          </div>
          
          <!-- Assign To Filter -->
          <div class="col-xl-2 col-md-3 mb-0" style="flex: 0 0 13%; max-width: 13%;">
            <v-select
              class="w-100"
              placeholder="Assigned To"
              :options="assigneeOptions"
              :value="filters.assigneeObject"
              @input="onAssigneeSelected"
              code="id"
              label="displayName"
              clearable
              filterable
            />
          </div>
          
          <!-- Due Date Filter -->
          <div class="col-xl-1 col-md-2 mb-0" style="flex: 0 0 195px; max-width: 195px;">
            <v-select
              class="w-100"
              placeholder="Due Date"
              :options="dueDateOptions"
              :value="filters.dueDateObject"
              @input="onDueDateSelected"
              code="id"
              label="displayName"
              clearable
              filterable
            />
          </div>
          
          <!-- Refresh Button -->
          <div class="col-xl-1 col-md-4 mb-0 d-flex ms-auto">
            <button 
              class="btn btn-sm btn-refresh-link w-100 h-100" 
              @click="onRefresh" 
              title="Refresh"
            >
              <i class="fa fa-refresh me-1"></i>
              <span class="refresh-text">Refresh</span>
            </button>
          </div>
        </div>
        
        <!-- Filter Summary -->
        <div v-if="hasActiveFilters" class="row mt-2">
          <div class="col-12">
            <div class="d-flex align-items-center justify-content-between">
              <small class="text-muted">
                <i class="fa fa-filter me-1"></i>
                {{ activeFiltersCount }} filter(s) active
              </small>
              <button 
                class="btn-clear-filters" 
                @click="clearAllFilters"
                title="Clear all filters"
              >
                <i class="fa fa-times me-1"></i>Clear All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'BoardFilters',
  computed: {
    ...mapState('board', [
      'filters',
      'filterOptions',
      'lastRefresh'
    ]),

    clientOptions() {
      return (this.filterOptions && this.filterOptions.clients) || [];
    },

    jobTypeOptions() {
      return (this.filterOptions && this.filterOptions.jobTypes) || [];
    },

    assigneeOptions() {
      return (this.filterOptions && this.filterOptions.assignees) || [];
    },

    dueDateOptions() {
      return (this.filterOptions && this.filterOptions.dueDates) || [];
    },

    hasActiveFilters() {
      return this.activeFiltersCount > 0;
    },

    activeFiltersCount() {
      return Object.values(this.filters).filter(value => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.trim() !== '';
        if (typeof value === 'number') return value !== 0;
        return Boolean(value);
      }).length;
    }
  },

  data() {
    return {
      jobNumberDebounceTimer: null,
      addressDebounceTimer: null
    };
  },
  
  methods: {
    ...mapActions('board', [
      'updateFilter',
      'resetFilters',
      'refreshBoard',
      'loadFilterOptions'
    ]),

    // Event handlers
    onClientSelected(event) {
      const value = event ? event.id : '';
      const objectValue = event || null;
      this.updateFilter({ filterType: 'client', value, objectValue });
    },

    onJobTypeSelected(event) {
      const value = event ? event.id : '';
      const objectValue = event || null;
      this.updateFilter({ filterType: 'jobType', value, objectValue });
    },

    onAssigneeSelected(event) {
      const value = event ? event.id : '';
      const objectValue = event || null;
      this.updateFilter({ filterType: 'assignee', value, objectValue });
    },

    onDueDateSelected(event) {
      const value = event ? event.id : '';
      const objectValue = event || null;
      this.updateFilter({ filterType: 'dueDate', value, objectValue });
    },

    onJobNumberChange(event) {
      const inputValue = event.target.value.trim();
      
      // Clear existing debounce timer
      if (this.jobNumberDebounceTimer) {
        clearTimeout(this.jobNumberDebounceTimer);
      }
      
      // If input is empty, trigger immediate refresh to show all jobs
      if (inputValue === '') {
        this.updateFilter({ filterType: 'jobNumber', value: '' });
        this.$store.dispatch('board/applyClientSideJobNumberFilter', '');
        return;
      }
      
      // For non-empty input, debounce the update
      this.jobNumberDebounceTimer = setTimeout(() => {
        // Extract numbers from the input (e.g., "WO-2025-001" -> "2025001")
        const extractedNumbers = this.extractNumbersFromJobInput(inputValue);
        
        this.updateFilter({ filterType: 'jobNumber', value: inputValue });
        
        // Also trigger client-side filtering for immediate UI feedback
        this.$store.dispatch('board/applyClientSideJobNumberFilter', extractedNumbers);
      }, 500);
    },

    // Extract numbers from job input for faster client-side filtering
    extractNumbersFromJobInput(input) {
      if (!input) return '';
      
      // Remove all non-numeric characters and join the numbers
      const numbers = input.replace(/[^0-9]/g, '');
      return numbers;
    },

    onAddressChange(event) {
      const inputValue = event.target.value.trim();
      
      // Clear existing debounce timer
      if (this.addressDebounceTimer) {
        clearTimeout(this.addressDebounceTimer);
      }
      
      // If input is empty, trigger immediate refresh to show all jobs
      if (inputValue === '') {
        this.updateFilter({ filterType: 'address', value: '' });
        return;
      }
      
      // For non-empty input, debounce the update
      this.addressDebounceTimer = setTimeout(() => {
        this.updateFilter({ filterType: 'address', value: inputValue });
      }, 500);
    },

    onRefresh() {
      this.refreshBoard();
    },

    clearAllFilters() {
      this.resetFilters();
    }
  },

  mounted() {
    this.loadFilterOptions();
  },

  beforeDestroy() {
    // Clean up debounce timers to prevent memory leaks
    if (this.jobNumberDebounceTimer) {
      clearTimeout(this.jobNumberDebounceTimer);
    }
    if (this.addressDebounceTimer) {
      clearTimeout(this.addressDebounceTimer);
    }
  }
};
</script>

<style scoped>
/* Space above filters so they don't touch the tabs */
.board-filters-row {
  padding-top: 0.75rem;
}

/* Filters container styling */
.board-filters-card {
  padding: 0.6rem 0.85rem;
  border: none;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04);
}

/* Modern blue refresh link-style control */
.btn-refresh-link {
  border: 1px solid #d0d7e2;
  background: #ffffff;
  color: #0d62ff !important;
  border-radius: 4px;
  padding: 6px 0;
  font-weight: 400;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  line-height: 1;
  box-shadow: none;
  transition: background .15s ease, border-color .15s ease, color .15s ease;
  min-height: 38px;
  box-sizing: border-box;
}

.board-filters-card .form-control { 
  height: 38px; 
  border-radius: 4px; 
}

.board-filters-card .vs__dropdown-toggle { 
  min-height: 38px; 
  border-radius: 4px; 
}

.board-filters-row-inner > div { 
  display: flex; 
  flex-direction: column; 
}

/* Reduce horizontal spacing between filters by ~50% */
.board-filters-row-inner > [class*='col-'] { 
  padding-left: 0.375rem; 
  padding-right: 0.375rem; 
}

/* Responsive width adjustments for job number and job type filters */
.board-filters-row-inner .job-number-filter {
  flex: 0 0 calc(10% + 3vw) !important;
  max-width: calc(10% + 3vw) !important;
}

.board-filters-row-inner .job-type-filter {
  flex: 0 0 calc(11% + 2vw) !important;
  max-width: calc(11% + 2vw) !important;
}

.btn-refresh-link:hover {
  background: #f5f9ff;
  border-color: #b5c2d3;
}

.btn-refresh-link:active {
  background: #e9f2ff;
}

.btn-refresh-link:focus {
  outline: none;
  border-color: #0d62ff;
  box-shadow: 0 0 0 2px rgba(13,98,255,0.25);
}

.btn-refresh-link .fa-refresh {
  font-size: 12px;
}

/* Disabled state */
.btn-refresh-link:disabled {
  opacity: 0.6;
  pointer-events: none;
}

/* Clear filters button styling */
.btn-clear-filters {
  background: none !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
  color: #007bff !important;
  font-size: 12px !important;
  cursor: pointer !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 2px !important;
  box-shadow: none !important;
  outline: none !important;
}

.btn-clear-filters:hover {
  color: #0056b3 !important;
  text-decoration: underline !important;
  background: none !important;
  border: none !important;
  box-shadow: none !important;
}

.btn-clear-filters:focus {
  outline: none !important;
  box-shadow: none !important;
  background: none !important;
  border: none !important;
}

.btn-clear-filters:active {
  background: none !important;
  border: none !important;
  box-shadow: none !important;
}

/* Auto refresh button styling */
.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-outline-secondary {
  color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-secondary:hover {
  color: #fff;
  background-color: #6c757d;
  border-color: #6c757d;
}

/* Dropdown menu styling */
.vs__dropdown-menu {
  max-height: 50vh !important;
  overflow-y: auto !important;
  z-index: 9999 !important;
}

.vs__dropdown-option {
  padding: 8px 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
